import React, { useEffect, useState } from "react";
import { Box, Typography, Paper } from "@mui/material";
import { styled } from "@mui/material/styles";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";

interface Currency {
  country: string;
  currency: string;
  sell: number;
  buy: number;
  date: string;
}

const TickerContainer = styled(Paper)(({ theme }) => ({
  width: "100%",
  overflow: "hidden",
  background: `linear-gradient(90deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 50%, ${theme.palette.primary.dark} 100%)`,
  color: theme.palette.primary.contrastText,
  padding: theme.spacing(1.5),
  marginBottom: theme.spacing(2),
  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
  "&:hover .ticker-content": {
    animationPlayState: "paused",
  },
}));

const TickerContent = styled(Box)(({ theme }) => ({
  display: "flex",
  animation: "ticker 40s linear infinite",
  whiteSpace: "nowrap",
  "@keyframes ticker": {
    "0%": {
      transform: "translateX(100%)",
    },
    "100%": {
      transform: "translateX(-100%)",
    },
  },
  "&:hover": {
    animationPlayState: "paused",
  },
}));

const CurrencyItem = styled(Box)(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  marginRight: theme.spacing(6),
  padding: theme.spacing(0, 2),
  borderRight: `1px solid ${theme.palette.primary.light}`,
  "&:last-child": {
    borderRight: "none",
  },
  "&:hover": {
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    borderRadius: theme.spacing(1),
  },
}));

const CurrencyIcon = styled(CurrencyExchangeIcon)(({ theme }) => ({
  marginRight: theme.spacing(1),
  fontSize: "1.2rem",
  opacity: 0.9,
}));

const CurrencyTicker: React.FC = () => {
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCurrencies = async () => {
      try {
        const response = await fetch("/api/currencies/");
        if (!response.ok) {
          throw new Error("Failed to fetch currencies");
        }
        const data = await response.json();
        setCurrencies(data);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to load currencies"
        );
      }
    };

    fetchCurrencies();
    // Refresh every 5 minutes
    const interval = setInterval(fetchCurrencies, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  if (error) {
    return null; // Silently fail if there's an error
  }

  return (
    <TickerContainer elevation={3}>
      <TickerContent className="ticker-content">
        {currencies.map((currency, index) => (
          <CurrencyItem key={`${currency.country}-${index}`}>
            <CurrencyIcon />
            <Typography
              variant="body1"
              component="span"
              sx={{ fontWeight: 500 }}
            >
              {currency.country} ({currency.currency})
            </Typography>
            <Typography
              variant="body1"
              component="span"
              sx={{
                ml: 2,
                color: "success.light",
                fontWeight: 600,
              }}
            >
              Sell: {currency.sell.toFixed(2)}
            </Typography>
            <Typography
              variant="body1"
              component="span"
              sx={{
                ml: 2,
                color: "error.light",
                fontWeight: 600,
              }}
            >
              Buy: {currency.buy.toFixed(2)}
            </Typography>
          </CurrencyItem>
        ))}
      </TickerContent>
    </TickerContainer>
  );
};

export default CurrencyTicker;
